import React, { useState, useEffect } from 'react';
import { useNavigate, Link, NavLink, useLocation } from 'react-router-dom';
import { Navbar, Nav, Dropdown } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Images } from '../../../assets/assets';
import SvgIcon from '../SvgIcon/SvgIcon';
import authService from '../../../services/authService';
import profileService from '../../../services/profileService';
import './AdminHeader.css';

const adminTabs = [
    { to: '/admin', label: 'Dashboard', Icon: Images.DashboardIcon, ActiveIcon: Images.DashboardIconFill },
    { to: '/admin/analysis', label: 'AI Analysis', Icon: Images.AnalysisIcon },
    { to: '/admin/progression', label: 'Progression', Icon: Images.ProgressionIcon },
    { to: '/admin/insights', label: 'AI Insights', Icon: Images.InsightsIcon },
    { to: '/admin/biomarkers', label: 'Biomarkers', Icon: Images.InsightsIcon },
    { to: '/admin/reports', label: 'Reports', Icon: Images.ReportsIcon },
    { to: '/admin/users', label: 'Users', Icon: Images.UsersIcon, ActiveIcon: Images.UsersIcon },
];

export default function AdminHeader() {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [adminInfo, setAdminInfo] = useState({
        name: 'System Administrator',
        email: 'admin@example.com'
    });
    const [profilePicture, setProfilePicture] = useState(null);

    useEffect(() => {
        // Get admin info from localStorage or API
        const admin = authService.getCurrentAdmin();
        if (admin) {
            // Try to get admin data from localStorage or make API call
            const adminData = localStorage.getItem('adminData');
            if (adminData) {
                try {
                    const parsedData = JSON.parse(adminData);
                    setAdminInfo({
                        name: parsedData.fullname || 'System Administrator',
                        email: parsedData.email || 'admin@example.com'
                    });
                } catch (error) {
                    console.error('Error parsing admin data:', error);
                }
            }
        }
        
        // Fetch profile picture
        const fetchProfilePicture = async () => {
            try {
                const response = await profileService.getProfile();
                if (response.success && response.data && response.data.profilePicture) {
                    const imageUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000'}/uploads/${response.data.profilePicture}`;
                    setProfilePicture(imageUrl);
                }
            } catch (error) {
                console.error('Error fetching profile picture:', error);
                // Keep default static image on error
            }
        };
        
        fetchProfilePicture();
    }, []);

    // Check patient data on route changes
    useEffect(() => {
        const patientData = sessionStorage.getItem('selectedPatient');
        if (patientData) {
            try {
                const patient = JSON.parse(patientData);
                setSelectedPatient(patient);
            } catch (error) {
                console.error('Error parsing patient data:', error);
                setSelectedPatient(null);
            }
        } else {
            setSelectedPatient(null);
        }
    }, [location.pathname]);

    // Check for selected patient data
    useEffect(() => {
        const checkPatientData = () => {
            const patientData = sessionStorage.getItem('selectedPatient');
            if (patientData) {
                try {
                    const patient = JSON.parse(patientData);
                    setSelectedPatient(patient);
                } catch (error) {
                    console.error('Error parsing patient data:', error);
                    setSelectedPatient(null);
                }
            } else {
                setSelectedPatient(null);
            }
        };

        // Check initially
        checkPatientData();

        // Listen for custom events
        const handlePatientSelected = () => {
            checkPatientData();
        };

        const handleLogout = () => {
            setSelectedPatient(null);
        };

        // Listen for custom events
        window.addEventListener('patientSelected', handlePatientSelected);
        window.addEventListener('userLogout', handleLogout);
        
        // Listen for window focus (when user comes back to tab)
        const handleFocus = () => {
            checkPatientData();
        };
        window.addEventListener('focus', handleFocus);

        // Also check periodically (every 500ms) to catch any missed updates
        const interval = setInterval(checkPatientData, 500);

        // Listen for profile picture updates
        const handleProfileUpdate = async () => {
            try {
                const response = await profileService.getProfile();
                if (response.success && response.data && response.data.profilePicture) {
                    const imageUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000'}/uploads/${response.data.profilePicture}`;
                    setProfilePicture(imageUrl);
                }
            } catch (error) {
                console.error('Error fetching updated profile picture:', error);
            }
        };
        
        window.addEventListener('profileUpdated', handleProfileUpdate);

        return () => {
            window.removeEventListener('patientSelected', handlePatientSelected);
            window.removeEventListener('userLogout', handleLogout);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('profileUpdated', handleProfileUpdate);
            clearInterval(interval);
        };
    }, []);

    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            await authService.logoutAdmin();
            // Clear sessionStorage and dispatch logout event
            sessionStorage.removeItem('selectedPatient');
            window.dispatchEvent(new CustomEvent('userLogout'));
            toast.success('Admin logged out successfully');
            navigate('/login', { replace: true });
        } catch (error) {
            console.error('Admin logout error:', error);
            // Even if logout fails on server, clear local token and redirect
            localStorage.removeItem('adminAccessToken');
            sessionStorage.removeItem('selectedPatient');
            window.dispatchEvent(new CustomEvent('userLogout'));
            navigate('/login', { replace: true });
        } finally {
            setIsLoggingOut(false);
        }
    };

    return (
        <header className='header'>
            <div className='nav-top'>
                <Link to="/admin" className="brand-logo">
                    <img src={Images.SiteLogo} alt="Logo" className="img-fluid" />
                </Link>
                <Dropdown className="user-dropdown">
                    <Dropdown.Toggle 
                        as="div" 
                        className="user-avatar" 
                        aria-label="Admin menu"
                        style={{ cursor: 'pointer' }}
                    >
                        <img 
                            src={profilePicture || Images.UserProfile} 
                            alt="Admin" 
                            className="img-fluid rounded-circle" 
                            onError={(e) => {
                                // Fallback to static image if profile picture fails to load
                                e.target.src = Images.UserProfile;
                            }}
                        />
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end" className="user-menu">
                        <Dropdown.ItemText className="user-info">
                            <div className="user-name">{adminInfo.name}</div>
                            <div className="user-email">{adminInfo.email}</div>
                        </Dropdown.ItemText>
                        <Dropdown.Divider />
                        <Dropdown.Item as={Link} to="/admin/profile">
                            Profile
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={handleLogout} disabled={isLoggingOut}>
                            {isLoggingOut ? 'Logging out...' : 'Logout'}
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
            <div className="nav-bottom">
                <Navbar bg="white" className="navbar" expand="md">
                    <Nav variant="pills" className="gap-2 flex-wrap">
                        {adminTabs.map((t) => {
                            // Tabs that don't require patient selection: Dashboard, Users, and Reports
                            const tabsWithoutPatientRequirement = ['/admin', '/admin/users', '/admin/reports'];
                            // Check if tab should be disabled
                            const isDisabled = !selectedPatient && !tabsWithoutPatientRequirement.includes(t.to);
                            const isAnalysisTab = t.to === '/admin/analysis';
                            
                            return (
                                <NavLink
                                    key={t.to}
                                    to={isDisabled ? '#' : t.to}
                                    end={t.to === '/admin'}
                                    className={({ isActive }) => 
                                        'nav-link' + 
                                        (isActive ? ' active' : '') + 
                                        (isDisabled ? ' disabled' : '')
                                    }
                                    onClick={(e) => {
                                        if (isDisabled) {
                                            e.preventDefault();
                                            if (isAnalysisTab) {
                                                alert('Please select a patient first by clicking the eye icon in the Patient Table.');
                                            } else {
                                                alert('Please select a patient first to access this feature.');
                                            }
                                        }
                                    }}
                                >
                                    {({ isActive }) => (
                                        <>
                                            {t.Icon ? (
                                                <SvgIcon
                                                    src={isActive ? t.ActiveIcon || t.Icon : t.Icon}
                                                    className="nav-icon"
                                                    data-default={t.Icon}
                                                    data-active={t.ActiveIcon || t.Icon}
                                                />
                                            ) : null}
                                            {t.label}
                                            {isDisabled && <span className="disabled-indicator">🔒</span>}
                                        </>
                                    )}
                                </NavLink>
                            );
                        })}
                    </Nav>
                </Navbar>
            </div>
        </header>
    );
}
